/**
 * 应用根组件 - 路由配置
 */
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { StoreProvider } from './contexts/StoreContext';

// 前台商城页面
import HomePage from './pages/storefront/HomePage';
import ProductDetailPage from './pages/storefront/ProductDetailPage';
import ProductsPage from './pages/storefront/ProductsPage';
import CategoryPage from './pages/storefront/CategoryPage';
import CartPage from './pages/storefront/CartPage';
import CheckoutPage from './pages/storefront/CheckoutPage';
import CheckoutResultPage from './pages/storefront/CheckoutResultPage';
import LoginPage from './pages/storefront/LoginPage';
import RegisterPage from './pages/storefront/RegisterPage';
import MyOrdersPage from './pages/storefront/MyOrdersPage';
import OrderDetailPage from './pages/storefront/OrderDetailPage';
import AccountPage from './pages/storefront/AccountPage';
import ForgotPasswordPage from './pages/storefront/ForgotPasswordPage';
import ResetPasswordPage from './pages/storefront/ResetPasswordPage';
import ProductVerifyPage from './pages/storefront/ProductVerifyPage';

// 后台管理页面
import AdminLayout from './pages/admin/AdminLayout';
import DashboardPage from './pages/admin/DashboardPage';
import ProductListPage from './pages/admin/ProductListPage';
import ProductFormPage from './pages/admin/ProductFormPage';
import OrderListPage from './pages/admin/OrderListPage';
import AdminOrderDetailPage from './pages/admin/OrderDetailPage';
import CustomerListPage from './pages/admin/CustomerListPage';
import CustomerDetailPage from './pages/admin/CustomerDetailPage';
import CustomerTagListPage from './pages/admin/CustomerTagListPage';
import CustomerSegmentListPage from './pages/admin/CustomerSegmentListPage';
import DiscountListPage from './pages/admin/DiscountListPage';
import SettingsPage from './pages/admin/SettingsPage';
import EmailCampaignListPage from './pages/admin/EmailCampaignListPage';
import CategoryListPage from './pages/admin/CategoryListPage';
import AbandonedOrderListPage from './pages/admin/AbandonedOrderListPage';
import AbandonedOrderDetailPage from './pages/admin/AbandonedOrderDetailPage';
import InventoryPage from './pages/admin/InventoryPage';
import FileManagePage from './pages/admin/FileManagePage';
import RecentCartListPage from './pages/admin/RecentCartListPage';

// 移动端后台页面
import MobileLayout from './pages/mobile/MobileLayout';
import MobileLoginPage from './pages/mobile/MobileLoginPage';
import MobileDashboardPage from './pages/mobile/MobileDashboardPage';
import MobileOrderListPage from './pages/mobile/MobileOrderListPage';
import MobileOrderDetailPage from './pages/mobile/MobileOrderDetailPage';
import MobileProductListPage from './pages/mobile/MobileProductListPage';
import MobileProductFormPage from './pages/mobile/MobileProductFormPage';
import MobileCustomerListPage from './pages/mobile/MobileCustomerListPage';
import MobileCustomerDetailPage from './pages/mobile/MobileCustomerDetailPage';
import MobileSettingPage from './pages/mobile/MobileSettingPage';
import MobileAbandonedOrderListPage from './pages/mobile/MobileAbandonedOrderListPage';
import MobileRecentCartListPage from './pages/mobile/MobileRecentCartListPage';
import MobileDiscountListPage from './pages/mobile/MobileDiscountListPage';
import MobileCategoryListPage from './pages/mobile/MobileCategoryListPage';
import MobileCustomerTagListPage from './pages/mobile/MobileCustomerTagListPage';
import MobileCustomerSegmentListPage from './pages/mobile/MobileCustomerSegmentListPage';
import MobileCustomerSegmentCustomersPage from './pages/mobile/MobileCustomerSegmentCustomersPage';
import MobileEmailCampaignListPage from './pages/mobile/MobileEmailCampaignListPage';
import MobileEmailCampaignDetailPage from './pages/mobile/MobileEmailCampaignDetailPage';
import MobileMorePage from './pages/mobile/MobileMorePage';
import MobileInventoryPage from './pages/mobile/MobileInventoryPage';
import MobileFileManagePage from './pages/mobile/MobileFileManagePage';

// 中式配色
const COLORS = {
  paper: '#F8F6F1',
  teaBrown: '#7D6B55',
  bambooGreen: '#6B8E7C',
  cinnabar: '#C44536',
};

/** 路由守卫 - 需要登录 */
function RequireAuth({ children }) {
  const { isLoggedIn, loading } = useAuth();
  if (loading) return null;
  return isLoggedIn ? children : <Navigate to="/login" replace />;
}

/** 路由守卫 - 需要管理员 */
function RequireAdmin({ children }) {
  const { isAdmin, isLoggedIn, loading } = useAuth();
  if (loading) return null;
  if (!isLoggedIn) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: COLORS.teaBrown,
          colorSuccess: COLORS.bambooGreen,
          colorDanger: COLORS.cinnabar,
          colorInfo: COLORS.teaBrown,
          colorWarning: '#D4B483',
          borderRadius: 8,
        },
        components: {
          Message: {
            colorBgContainer: '#fff',
            colorText: COLORS.ink,
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            borderRadius: 12,
            padding: '12px 20px',
          },
        },
      }}
    >
      <AuthProvider>
        <StoreProvider>
          <CartProvider>
            <Routes>
              {/* ===== 前台商城 ===== */}
              <Route path="/" element={<HomePage />} />
              <Route path="/products" element={<ProductsPage />} />
              <Route path="/products/:slug" element={<ProductDetailPage />} />
              <Route path="/categories/:slug" element={<CategoryPage />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/checkout/result" element={<CheckoutResultPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/orders" element={<RequireAuth><MyOrdersPage /></RequireAuth>} />
              <Route path="/orders/:id" element={<RequireAuth><OrderDetailPage /></RequireAuth>} />
              <Route path="/account" element={<RequireAuth><AccountPage /></RequireAuth>} />
              <Route path="/relx-verify" element={<ProductVerifyPage />} />

              {/* ===== 后台管理 ===== */}
              <Route path="/admin" element={<RequireAdmin><AdminLayout /></RequireAdmin>}>
                <Route index element={<DashboardPage />} />
                <Route path="products" element={<ProductListPage />} />
                <Route path="products/new" element={<ProductFormPage />} />
                <Route path="products/:id/edit" element={<ProductFormPage />} />
                {/* 兼容不带 /edit 的链接，自动重定向 */}
                <Route path="products/:id" element={<ProductFormPage />} />
                <Route path="categories" element={<CategoryListPage />} />
                <Route path="orders" element={<OrderListPage />} />
                <Route path="orders/:id" element={<AdminOrderDetailPage />} />
                <Route path="abandoned-orders" element={<AbandonedOrderListPage />} />
                <Route path="abandoned-orders/:id" element={<AbandonedOrderDetailPage />} />
                <Route path="recent-carts" element={<RecentCartListPage />} />
                <Route path="customers" element={<CustomerListPage />} />
                <Route path="customers/:id" element={<CustomerDetailPage />} />
                <Route path="customer-tags" element={<CustomerTagListPage />} />
                <Route path="customer-segments" element={<CustomerSegmentListPage />} />
                <Route path="email-campaigns" element={<EmailCampaignListPage />} />
                <Route path="discounts" element={<DiscountListPage />} />
                <Route path="inventory" element={<InventoryPage />} />
                <Route path="files" element={<FileManagePage />} />
                <Route path="settings" element={<SettingsPage />} />
              </Route>

              {/* ===== 移动端后台 ===== */}
              <Route path="/admin/m/login" element={<LoginPage />} />
              <Route path="/admin/m" element={<RequireAdmin><MobileLayout /></RequireAdmin>}>
                <Route index element={<MobileDashboardPage />} />
                <Route path="orders" element={<MobileOrderListPage />} />
                <Route path="orders/:id" element={<MobileOrderDetailPage />} />
                <Route path="abandoned-orders" element={<MobileAbandonedOrderListPage />} />
                <Route path="recent-carts" element={<MobileRecentCartListPage />} />
                <Route path="products" element={<MobileProductListPage />} />
                <Route path="products/new" element={<MobileProductFormPage />} />
                <Route path="products/:id/edit" element={<MobileProductFormPage />} />
                <Route path="products/:id" element={<MobileProductFormPage />} />
                <Route path="categories" element={<MobileCategoryListPage />} />
                <Route path="discounts" element={<MobileDiscountListPage />} />
                <Route path="customers" element={<MobileCustomerListPage />} />
                <Route path="customers/:id" element={<MobileCustomerDetailPage />} />
                <Route path="customer-tags" element={<MobileCustomerTagListPage />} />
                <Route path="customer-segments" element={<MobileCustomerSegmentListPage />} />
                <Route path="customer-segments/:id/customers" element={<MobileCustomerSegmentCustomersPage />} />
                <Route path="email-campaigns" element={<MobileEmailCampaignListPage />} />
                <Route path="email-campaigns/:id" element={<MobileEmailCampaignDetailPage />} />
                <Route path="inventory" element={<MobileInventoryPage />} />
                <Route path="files" element={<MobileFileManagePage />} />
                <Route path="more" element={<MobileMorePage />} />
                <Route path="settings" element={<MobileSettingPage />} />
              </Route>
            </Routes>
          </CartProvider>
        </StoreProvider>
      </AuthProvider>
    </ConfigProvider>
  );
}
